/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';

import template from './search-select.component.html';
import {OBJECT_TYPE} from '../constants/object-type';
import {objectTypeStateParams} from '../filters/object-type-state-params';
import {AdnSelectItemLink, AdnSelectSearchElement} from '../directives/adn-select/adn-select.component';
import {OgTranslateService} from '../filters/og-translate.service';
import {OrderResource} from '../../../components/api/resources/resource/order.resource';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {CreativeSetResource} from '../../../components/api/resources/resource/creative-set.resource';
import {LineItemResource} from '../../../components/api/resources/resource/line-item.resource';
import {AdnFormStateService} from '../../../components/util/adn-form-state.service';
import {SearchResource} from '../../../components/api/resources/resource/search';
import {deferMarkForCheck} from '../util/defer-mark-for-check';

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  if (value === '' || value === 'multiple') {
    return true;
  }
  return defaultValue;
}

@Component({
  standalone: false,
  selector: 'adn-search-select',
  template
})
export class SearchSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: any;
  @Input() objectType = '';
  @Input() selectObj: any;
  @Input() objParam = '';
  @Input() allowClear: boolean | string;
  @Input() required: boolean | string;
  @Input() fieldName = '';
  @Input() transText = '';
  @Input() onSelectParam = '';
  @Input() multiple: boolean | string;
  @Input() special = '';
  @Input() specialObj: any;
  @Input() disabled = false;

  ready = false;
  param = '';
  transParam = '';
  dataResults: any[] = [];

  searchHandler = (searchElement: AdnSelectSearchElement): void => {
    this.runSearch(searchElement);
  };

  selectedItemLink = (item: any): AdnSelectItemLink | null => {
    if (!this.multipleValue || !this.objectTypeDef?.stateName || !item) {
      return null;
    }

    const params = objectTypeStateParams(item, this.objectTypeDef);
    if (!params || _.every(params, (value) => value === undefined || value === null || value === '')) {
      return null;
    }

    return {
      stateName: this.objectTypeDef.stateName,
      params: params
    };
  };

  private objectTypeDef: any;
  private prevSearchText: string | null = null;
  private prevAdv: string | null = null;
  private searchTimeout: any;
  private destroyed = false;

  constructor(
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(CreativeSetResource) private readonly CreativeSet: any,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(TeamResource) private readonly Team: any,
    private readonly ogTranslate: OgTranslateService,
    @Inject(AdnFormStateService) private readonly adnFormState: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get resolvedObjectType(): string {
    return this.objectType;
  }

  get resolvedObjParam(): string {
    return this.objParam;
  }

  get resolvedFieldName(): string {
    if (this.fieldName) {
      return this.fieldName;
    }
    if (this.resolvedObjParam) {
      return this.resolvedObjParam;
    }
    if (this.objectTypeDef?.apiParam) {
      return this.objectTypeDef.apiParam;
    }
    return 'searchSelect';
  }

  get resolvedTransText(): string {
    return this.transText;
  }

  get resolvedOnSelectParam(): string {
    return this.onSelectParam;
  }

  get resolvedSelectObj(): any {
    return this.selectObj;
  }

  get resolvedSpecialObj(): any {
    return this.specialObj;
  }

  get allowClearValue(): boolean {
    const value = this.allowClear;
    return coerceBooleanAttr(value);
  }

  get requiredValue(): boolean {
    return coerceBooleanAttr(this.required);
  }

  get multipleValue(): boolean {
    return coerceBooleanAttr(this.multiple);
  }

  get placeholderText(): string {
    if (this.resolvedTransText) {
      return this.resolvedTransText;
    }
    const translated = this.ogTranslate.transform(this.transParam);
    return 'Select ' + String(translated || this.transParam).toLowerCase();
  }

  get labelFallback(): string {
    return _.get(this.model, [this.param, 'name'], '');
  }

  ngOnInit(): void {
    this.configureFromObjectType();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.objectType || changes.objParam) {
      this.configureFromObjectType();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  handleSelect(): void {
    if (_.isFunction(this.adnFormState?.markDirty)) {
      this.adnFormState.markDirty();
    }
    const selectObj = _.get(this.resolvedSelectObj, 'onSelect') ? this.resolvedSelectObj : {onSelect: _.noop};
    selectObj.onSelect(this.resolvedOnSelectParam);
  }

  private configureFromObjectType(): void {
    this.objectTypeDef = OBJECT_TYPE[this.resolvedObjectType];
    if (!this.objectTypeDef) {
      this.ready = false;
      return;
    }
    this.param = this.resolvedObjParam || this.objectTypeDef.apiParam;
    this.transParam = this.objectTypeDef.apiParam;
    this.ready = true;
  }

  private runSearch(searchElement: AdnSelectSearchElement): void {
    if (_.has(searchElement, 'open') && !searchElement.open) {
      return;
    }

    const advertiserId = _.get(this.resolvedSpecialObj, ['id']);
    if (this.prevSearchText === searchElement.search && this.prevAdv === advertiserId) {
      return;
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.searchTimeout = null;
      if (this.destroyed) {
        return;
      }

      if (this.special && this.special.indexOf('ADVERTISER') > -1) {
        this.searchAdvertiserScoped(searchElement.search, advertiserId);
      } else if (this.special && this.special.indexOf('TEAM') > -1) {
        this.searchTeams(searchElement.search);
      } else {
        this.searchDefault(searchElement.search);
      }
    }, 150);
  }

  private searchAdvertiserScoped(search: string, advertiserId: string): void {
    const params: any = {
      filterBy: 'name',
      filterByLike: search,
      pageSize: 50
    };
    if (advertiserId) {
      params.advertiser = advertiserId;
    }

    let resource = this.CreativeSet;
    if (this.special === 'LINE_ITEM_ADVERTISER') {
      resource = this.LineItem;
    } else if (this.special === 'ORDER_ADVERTISER') {
      resource = this.Order;
    }

    this.prevSearchText = search;
    this.prevAdv = advertiserId;
    resource.query(params, (data: any) => {
      this.applyDataResults(this.mapQueryResults(data));
    });
  }

  private searchTeams(search: string): void {
    const params: any = {
      filterBy: 'name',
      filterByLike: search,
      minimal: true,
      pageSize: 50
    };
    if (this.special.indexOf('TEAM_MA') > -1) {
      params.where = ['team.type in STANDARD,AX_ADVERTISER'];
    }

    this.prevSearchText = search;
    this.Team.query(params, (data: any) => {
      const results = _.get(data, 'results');
      if (!_.isArray(results) || results.length === 0) {
        this.applyDataResults(this.emptyResultsMessage());
        return;
      }
      const modelIds = _.map(_.get(this.model, this.param) || [], 'id');
      this.applyDataResults(_.filter(results, (result: any) => {
        return modelIds.indexOf(result.id) < 0;
      }));
    });
  }

  private searchDefault(search: string): void {
    this.prevSearchText = search;
    this.prevAdv = null;
    this.searchResource.query({q: search, types: this.objectTypeDef.type, pageSize: 50}).then((data: any) => {
      this.applyDataResults(data?.searchResults || []);
    }).catch(() => {
      this.applyDataResults([]);
    });
  }

  /** Defer — [items]="dataResults" flipping []→results mid-CD causes NG0100. */
  private applyDataResults(results: any[]): void {
    if (this.destroyed) {
      return;
    }
    deferMarkForCheck(this.cdr, () => {
      this.dataResults = results;
    });
  }

  private mapQueryResults(data: any): any[] {
    const results = _.get(data, 'results');
    if (!_.isArray(results) || results.length === 0) {
      return this.emptyResultsMessage();
    }
    return results;
  }

  private emptyResultsMessage(): any[] {
    return [{name: 'No matching results found'}];
  }
}
